import { useEffect, useRef, useState } from "react";
import { Box, Tabs, Tab, Paper, useTheme } from "@mui/material";
import CIS_img from "../../assets/baseVersion/CIS_img.svg";
import Spark_img from "../../assets/baseVersion/Details_Spark.svg";
import DISA_img from "../../assets/baseVersion/DISA.svg";
import Nist_img from "../../assets/baseVersion/Nist.svg";
import Hippa_img from "../../assets/baseVersion/hippa.svg";
import Pci_img from "../../assets/baseVersion/pcidss.svg";
import Mitre_img from "../../assets/baseVersion/mitre.svg";
import { useDispatch, useSelector } from "react-redux";

import axiosInstance from "../../axios/axios";
import SkeletonLoader from "../../modules/dashboard/shared/skeleton_loader";
import CIS_Dark from "../../assets/baseVersion/CIS_Dark.svg";
import Nist_Dark from "../../assets/baseVersion/Nist_Dark.svg";
import Mitre_Dark from "../../assets/baseVersion/Mitre_Dark.svg"
import Spark_Dark from "../../assets/baseVersion/Spark_Dark.svg"
import Hippa_Dark from "../../assets/baseVersion/Hippa_Dark.svg"
import { setButtonSelected, setCategorySelected } from "../SecurityPosture/TabSelection-slice";

interface TabSelectionProps {
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  Selected_Rowid?: string;
  module?: string;
  controlType?: string | string[];
}

const TabSelection: React.FC<TabSelectionProps> = ({ setLoading, Selected_Rowid, module, controlType }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [tabsData, setTabsData] = useState<{ label: string; icon: string; category: string }[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [isBenchVersions, setBenchVersions] = useState<any>();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  let resolvedModule = "";
  if (location.pathname === "/on-premises/security-posture") {
    resolvedModule = "on_premise";
  } else if (location.pathname === "/cloud/security-posture") {
    resolvedModule = "cloud";
  } else if (module === "on_premise" || module === "cloud") {
    resolvedModule = module;
  } else {
    resolvedModule = "on_premise";
  }

  const dispatch = useDispatch();
  // The clicked control's type (the row click stores it); read, not a dependency,
  // when the vendor list loads -- see the cloud case below.
  const selectedButton = useSelector(
    (state: { OnPrem_DetailsSelectedTab: { selectedButton: string } }) =>
      state.OnPrem_DetailsSelectedTab.selectedButton
  );
  const selectedControlType = useSelector(
    (state: { OnPrem_DetailsSelectedTab: { selectedControlType: string } }) =>
      state.OnPrem_DetailsSelectedTab.selectedControlType
  );
  const selectedButtonRef = useRef(selectedButton);
  selectedButtonRef.current = selectedButton;
  const cloudControlType = controlType || selectedControlType;

  const iconMap: Record<string, string> = {
    CIS: isDarkMode ? CIS_Dark : CIS_img,
    SPARK: isDarkMode ? Spark_Dark : Spark_img,
    DISA: DISA_img,
    NIST: isDarkMode ? Nist_Dark : Nist_img,
    HIPPA: isDarkMode ? Hippa_Dark : Hippa_img,
    PCI: Pci_img,
    "PCI DSS": Pci_img,
    MITRE: isDarkMode ? Mitre_Dark : Mitre_img,
  };

  // Fetch unique control_display_name values from vendor-device-versions API
  // This is the correct source for tabs (same as SecurityPosture TabSelection)
  useEffect(() => {
    const fetchVendorDeviceVersions = async () => {
      if (!Selected_Rowid) return;

      try {
        const category_name = "Benchmark";
        const response = await axiosInstance.get(
          `securityscan/vendor-device-versions/${Selected_Rowid}?module=${resolvedModule}&category_name=${category_name}`
        );
        const rawData = response?.data?.data;

        // Cloud: vendor-device-versions answers with a flat list of vendor names and no
        // control types, so there is nothing to parse (treating it as vendor -> model ->
        // versions threw, and no tab was ever built). The Benchmark tab is then the
        // clicked control's own type.
        if (Array.isArray(rawData)) {
          const clicked = selectedButtonRef.current;
          setBenchVersions(rawData.length > 0 && clicked ? [clicked.toUpperCase()] : []);
          return;
        }

        // Use a Set to store unique control_display_name values (no duplicates)
        const controlDisplayNamesSet = new Set<string>();

        Object.values(rawData || {}).forEach((deviceObj: any) => {
          Object.values(deviceObj).forEach((versions: any) => {
            if (Array.isArray(versions)) {
              versions.forEach((item: any) => {
                if (item.control_display_name) {
                  controlDisplayNamesSet.add(item.control_display_name);
                }
              });
            }
          });
        });

        setBenchVersions(Array.from(controlDisplayNamesSet)); // Convert Set to array
      } catch (error) {
        console.error("Error fetching vendor-device-versions:", error);
        setBenchVersions([]);
      }
    };

    fetchVendorDeviceVersions();
  }, [Selected_Rowid]);

  // Build tabs from vendor-device-versions data (isBenchVersions)
  useEffect(() => {
    const buildTabs = async () => {
      if (!Selected_Rowid) return;
      // Wait for isBenchVersions to be fetched
      if (isBenchVersions === undefined) return;

      setIsLoading(true);
      setLoading(true);

      let dynamicTabs: { label: string; icon: string; category: string }[] = [];

      if (isBenchVersions && isBenchVersions.length > 0) {
        // Use control_display_name from vendor devices (no duplicates, correct source)
        dynamicTabs = isBenchVersions.map((benchmark: string) => {
          const label = benchmark;
          const icon = iconMap[label.toUpperCase()] || CIS_img;
          return { label, icon, category: "Benchmark" };
        });
      } else if (resolvedModule === "cloud" && cloudControlType) {
        // Same as cloud scan summary Details: use the row's control_type directly.
        const controlTypes = Array.isArray(cloudControlType)
          ? cloudControlType
          : String(cloudControlType)
              .split(",")
              .map((item: string) => item.trim())
              .filter(Boolean);

        dynamicTabs = controlTypes.map((ctrl: string) => {
          const label = ctrl.toUpperCase();
          const icon = iconMap[label] || CIS_img;
          return { label, icon, category: "Benchmark" };
        });
      } else {
        // Fallback to compliance-list if vendor-device-versions returned nothing
        try {
          const response = await axiosInstance.get(
            `securityscan/compliance-list?parameter_name_id=${Selected_Rowid}&module=${resolvedModule}`
          );
          const categories = response?.data?.data?.categories || [];

          const benchmark = categories.find(
            (cat: any) => cat.category_name === "Benchmark"
          );

          if (benchmark?.controls) {
            const seenLabels = new Set<string>();
            dynamicTabs = benchmark.controls
              .filter((control: any) => {
                const label = control.control_display_name;
                if (seenLabels.has(label)) return false;
                seenLabels.add(label);
                return true;
              })
              .map((control: any) => {
                const label = control.control_display_name;
                const icon = iconMap[label.toUpperCase()] || CIS_img;
                return { label, icon, category: "Benchmark" };
              });
          } else {
            // Collect controls from all categories with deduplication
            const seenLabels = new Set<string>();
            categories.forEach((cat: any) => {
              if (cat.controls) {
                cat.controls.forEach((control: any) => {
                  const label = control.control_display_name;
                  if (label && !seenLabels.has(label)) {
                    seenLabels.add(label);
                    const icon = iconMap[label.toUpperCase()] || CIS_img;
                    dynamicTabs.push({ label, icon, category: cat.category_name });
                  }
                });
              }
            });
          }
        } catch (error) {
          console.error("Error fetching compliance list:", error);
        }
      }

      setTabsData(dynamicTabs);
      setIsReady(true);
      setIsLoading(false);
      setLoading(false);
    };

    buildTabs();
  }, [Selected_Rowid, isBenchVersions, resolvedModule, cloudControlType]);

  // Reset isReady when dependencies change
  useEffect(() => {
    setIsReady(false);
  }, [Selected_Rowid]);

  useEffect(() => {
    setSelectedTab(0);
    if (tabsData.length > 0) {
      dispatch(setButtonSelected(tabsData[0].label.toLowerCase()));
      dispatch(setCategorySelected(tabsData[0].category));
    }
  }, [tabsData]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    dispatch(setButtonSelected(tabsData[newValue].label.toLowerCase()));
    dispatch(setCategorySelected(tabsData[newValue].category));
  };

  const getButtonWidth = (count: number) => {
    const widthMap: Record<number, number> = {
      1: 130,
      2: 210,
      3: 310,
      4: 420,
    };
    return `${widthMap[count] || 520}px`;
  };

  const ButtonWidth = getButtonWidth(tabsData.length);

  if (!isReady || isLoading || tabsData.length === 0) {
    return (
      <Paper
        elevation={3}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: isDarkMode ? "#191C24" : "#F1F1FF",
          p: 1,
          width: "200px",
          height: "60px",
          display: "flex",
          justifyContent: "flex-start",
        }}
      >
        <SkeletonLoader count={1} styles={{ height: 40, width: "100%" }} />
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: isDarkMode ? "#191C24" : "#F1F1FF",
        p: 0.5,
        maxWidth: ButtonWidth,
        width: "100%",
        display: "flex",
        justifyContent: "flex-start",
        position: "sticky",
      }}
    >
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{
          position: "relative",
          "& .MuiTabs-indicator": {
            height: "100%",
            backgroundColor: isDarkMode ? "#343842" : "white",
            borderRadius: "8px",
            transition: "all 0.3s ease-in-out",
            zIndex: 1,
          },
        }}
      >
        {tabsData.map((tab, index) => (
          <Tab
            key={index}
            label={
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap={1}
                position="relative"
                zIndex={2}
              >
                <img
                  src={tab.icon}
                  alt={tab.label}
                  width={85}
                  height={25}
                  style={{ cursor: tabsData.length > 1 ? "pointer" : "default" }}
                />
              </Box>
            }
            sx={{
              textTransform: "none",
              color: selectedTab === index ? "black" : "#555",
              transition: "color 0.3s ease-in-out",
              minWidth: "100px",
              maxWidth: "150px",
            }}
          />
        ))}
      </Tabs>
    </Paper>
  );
};

export default TabSelection;

